import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import toast from 'react-hot-toast';

// Configure axios defaults
axios.defaults.baseURL = process.env.REACT_APP_API_URL || '/api';
axios.defaults.withCredentials = true;

// Function to set auth header
const setAuthHeader = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    console.log('Set Authorization header:', `Bearer ${token.substring(0, 20)}...`);
  } else {
    delete axios.defaults.headers.common['Authorization'];
    console.log('Removed Authorization header');
  }
};

// Auth store with Zustand
export const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      setAuth: (user, token) => {
        set({ user, token, isAuthenticated: true, error: null });
        
        // Set axios default header
        setAuthHeader(token);
      },

      clearAuth: () => {
        set({ user: null, token: null, isAuthenticated: false, error: null });
        
        // Remove axios default header
        setAuthHeader(null);
      },

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error }),

      // Check if user is authenticated
      checkAuth: async () => {
        const { token } = get();
        
        if (!token) {
          // Don't call clearAuth() here -- zustand might not have hydrated yet
          // and we'd overwrite the saved token in localStorage
          return false;
        }

        try {
          // Set the token in axios headers
          setAuthHeader(token);
          
          // Verify token with backend
          const response = await axios.get('/auth/user');
          
          if (response.data.user) {
            set({ 
              user: response.data.user, 
              isAuthenticated: true, 
              error: null 
            });
            return true;
          } else {
            return false;
          }
        } catch (error) {
          console.error('Auth check failed:', error?.response?.status, error.message);
          return false;
        }
      },

      // Login with Salesforce
      loginWithSalesforce: async () => {
        try {
          set({ isLoading: true, error: null });
          
          const response = await axios.get('/auth/salesforce/authorize');
          
          if (response.data.authUrl) {
            // Redirect to Salesforce OAuth
            window.location.href = response.data.authUrl;
          } else {
            throw new Error('Failed to get authorization URL');
          }
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'Failed to initiate login';
          set({ error: errorMessage, isLoading: false });
          toast.error(errorMessage);
        }
      },

      // Login with Microsoft
      loginWithMicrosoft: async () => {
        try {
          set({ isLoading: true, error: null });
          
          const response = await axios.get('/auth/microsoft/authorize');
          
          if (response.data.authUrl) {
            window.location.href = response.data.authUrl;
          } else {
            throw new Error('Failed to get authorization URL');
          }
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'Failed to initiate login';
          set({ error: errorMessage, isLoading: false });
          toast.error(errorMessage);
        }
      },

      // Login with Rspamd
      loginWithRspamd: async () => {
        try {
          set({ isLoading: true, error: null });
          
          const response = await axios.get('/auth/rspamd/authorize');
          
          if (response.data.authUrl) {
            // Redirect to Rspamd OAuth
            window.location.href = response.data.authUrl;
          } else {
            throw new Error('Failed to get authorization URL');
          }
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'Failed to initiate login';
          set({ error: errorMessage, isLoading: false });
          toast.error(errorMessage);
        }
      },

      // Handle OAuth callback
      handleAuthCallback: async (token) => {
        try {
          set({ isLoading: true, error: null });
          
          if (!token) {
            throw new Error('No token received');
          }

          // Set token and get user info
          setAuthHeader(token);
          console.log('handleAuthCallback: fetching /auth/user');
          const response = await axios.get('/auth/user');
          console.log('handleAuthCallback: response', response.status, response.data);
          
          if (response.data.user) {
            get().setAuth(response.data.user, token);
            toast.success('Successfully logged in!');
            return true;
          } else {
            throw new Error('Failed to get user information');
          }
        } catch (error) {
          console.error('handleAuthCallback error:', error.message, error.response?.status, error.response?.data);
          const errorMessage = error.response?.data?.message || error.message || 'Authentication failed';
          set({ error: errorMessage, isLoading: false });
          toast.error(errorMessage);
          get().clearAuth();
          return false;
        } finally {
          set({ isLoading: false });
        }
      },

      // Logout
      logout: async () => {
        try {
          set({ isLoading: true });
          
          // Call backend logout endpoint
          await axios.post('/auth/logout');
          
          get().clearAuth();
          toast.success('Logged out successfully');
          
          // Redirect to login page
          window.location.href = '/login';
        } catch (error) {
          console.error('Logout error:', error);
          // Clear auth anyway
          get().clearAuth();
          window.location.href = '/login';
        } finally {
          set({ isLoading: false });
        }
      },

      // Refresh token
      refreshToken: async () => {
        try {
          const response = await axios.post('/auth/refresh');
          if (response.data.token) {
            const { user } = get();
            get().setAuth(user, response.data.token);
          }
          return true;
        } catch (error) {
          console.error('Token refresh failed:', error);
          get().clearAuth();
          return false;
        }
      },

      // Get user info
      getUserInfo: () => {
        const { user } = get();
        return user;
      },

      // Check if user has permission
      hasPermission: (permission) => {
        const { user, isAuthenticated } = get();
        
        if (!isAuthenticated || !user) {
          return false;
        }

        // In a real implementation, you'd check user permissions
        // For now, all authenticated users have all permissions
        return true;
      }
    }),
    {
      name: 'auth-storage',
      storage: {
        getItem: (name) => {
          const value = localStorage.getItem(name);
          if (!value) return null;
          try { return JSON.parse(value); } catch { return null; }
        },
        setItem: (name, value) => localStorage.setItem(name, JSON.stringify(value)),
        removeItem: (name) => localStorage.removeItem(name),
      },
      partialize: (state) => ({ 
        user: state.user, 
        token: state.token, 
        isAuthenticated: state.isAuthenticated 
      }),
      onRehydrateStorage: () => (state) => {
        setAuthHeader(state?.token);
      }
    }
  )
);

// Axios interceptor for handling 401 responses
// Skip interceptor for auth endpoints to avoid infinite loops
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { status } = error.response || {};
    const url = error.config?.url || '';
    
    // Don't intercept auth endpoints (prevents infinite refresh loop)
    // and don't intercept if we're already on the login page
    if (status === 401 && !url.includes('/auth/') && window.location.pathname !== '/login') {
      const { isAuthenticated, clearAuth } = useAuthStore.getState();
      
      if (isAuthenticated) {
        clearAuth();
        toast.error('Session expired. Please log in again.');
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Axios request interceptor to ensure token is always sent
axios.interceptors.request.use(
  (config) => {
    // Get current token from store
    const state = useAuthStore.getState();
    const token = state.token;
    
    // Set Authorization header if token exists and not already set
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Added Authorization header to request:', config.url);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default useAuthStore; 